/**
 * SchedaStatistiche — i conti di un questionario. Una persona conta una volta (solo le risposte
 * `attuale`: lo decide `calcolaStatistiche` di `shared/questionari`, la stessa regola ovunque).
 *
 * In cima: iscritti, aperture della pagina e quanti hanno finito, la somma di ogni domanda
 * numerica (per la lista d'attesa: gli utenti potenziali dichiarati), i consensi. Poi le
 * provenienze, l'andamento per giorno e ogni domanda con le sue barre.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { calcolaStatistiche, contaNeiConti, type StatisticheDomanda } from '@shared/questionari';
import { tokens } from '../../../theme';
import { millisDi, TIPO_DOMANDA_LABELS } from '../labels';
import type { DatiQuestionario } from './useDatiQuestionario';
import { BarreOrizzontali, ColonneGiornaliere, numero, Riquadro } from './Grafici';
import {
  inizioPeriodo, nelPeriodo, perStatistiche, PERIODO_LABELS, provenienzaDi, provenienzaSessione, statoConsenso,
  type Periodo,
} from './filtri';

const TUTTE = '__tutte__';

function percento(parte: number, totale: number): string {
  if (totale <= 0) return '—';
  return `${(Math.round((parte / totale) * 1000) / 10).toLocaleString('it-IT')}%`;
}

export const SchedaStatistiche: React.FC<{ dati: DatiQuestionario }> = ({ dati }) => {
  const [periodo, setPeriodo] = useState<Periodo>('tutto');
  const [provenienza, setProvenienza] = useState<string>(TUTTE);
  const domande = dati.normalizzato?.domande ?? [];

  const inizio = inizioPeriodo(periodo);
  const risposteFiltrate = useMemo(
    () => dati.risposte.filter((r) => nelPeriodo(r.completato_at, inizio) && (provenienza === TUTTE || provenienzaDi(r) === provenienza)),
    [dati.risposte, inizio, provenienza],
  );
  const sessioniFiltrate = useMemo(
    () => (dati.sessioni ?? []).filter((s) => nelPeriodo(s.creata_at, inizio) && (provenienza === TUTTE || provenienzaSessione(s) === provenienza)),
    [dati.sessioni, inizio, provenienza],
  );
  const statistiche = useMemo(() => calcolaStatistiche(domande, risposteFiltrate.map(perStatistiche)), [domande, risposteFiltrate]);

  const provenienzeNote = useMemo(() => {
    const tutte = new Set<string>();
    dati.risposte.forEach((r) => tutte.add(provenienzaDi(r)));
    (dati.sessioni ?? []).forEach((s) => tutte.add(provenienzaSessione(s)));
    return Array.from(tutte).sort((a, b) => a.localeCompare(b, 'it'));
  }, [dati.risposte, dati.sessioni]);

  const finite = sessioniFiltrate.filter((s) => s.response_id || s.usata_at).length;
  const attuali = risposteFiltrate.filter(contaNeiConti);
  const confermati = attuali.filter((r) => statoConsenso(dati.lead.get(r.platform_lead_id)) === 'confermato').length;
  const numeriche = statistiche.domande.filter((d) => d.numero);

  // Per provenienza: aperture, chi ha finito, iscritti attuali.
  const righeProvenienza = useMemo(() => {
    const mappa = new Map<string, { aperture: number; finite: number; iscritti: number }>();
    const riga = (p: string) => {
      if (!mappa.has(p)) mappa.set(p, { aperture: 0, finite: 0, iscritti: 0 });
      return mappa.get(p)!;
    };
    sessioniFiltrate.forEach((s) => {
      const r = riga(provenienzaSessione(s));
      r.aperture += 1;
      if (s.response_id || s.usata_at) r.finite += 1;
    });
    statistiche.per_provenienza.forEach((p) => { riga(p.provenienza).iscritti = p.conteggio; });
    return Array.from(mappa.entries()).sort((a, b) => b[1].iscritti - a[1].iscritti || b[1].aperture - a[1].aperture);
  }, [sessioniFiltrate, statistiche.per_provenienza]);

  const aperturePerGiorno = useMemo(() => {
    const m = new Map<string, number>();
    sessioniFiltrate.forEach((s) => {
      const ms = millisDi(s.creata_at);
      if (ms === null) return;
      const g = new Date(ms).toISOString().slice(0, 10);
      m.set(g, (m.get(g) ?? 0) + 1);
    });
    return m;
  }, [sessioniFiltrate]);

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Periodo" value={periodo} onChange={(e) => setPeriodo(e.target.value as Periodo)} sx={{ minWidth: 180 }}>
          {(Object.keys(PERIODO_LABELS) as Periodo[]).map((p) => <MenuItem key={p} value={p}>{PERIODO_LABELS[p]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Provenienza" value={provenienza} onChange={(e) => setProvenienza(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value={TUTTE}>Tutte</MenuItem>
          {provenienzeNote.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        {(periodo !== 'tutto' || provenienza !== TUTTE) && (
          <Button size="small" onClick={() => { setPeriodo('tutto'); setProvenienza(TUTTE); }}>Togli i filtri</Button>
        )}
      </Stack>

      {dati.erroreSessioni && (
        <Alert severity="info">
          Le aperture della pagina non sono leggibili ({dati.erroreSessioni}): le statistiche qui sotto contano solo gli iscritti.
        </Alert>
      )}

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        <Riquadro titolo="Iscritti" valore={numero(statistiche.totale)} nota="una persona conta una volta" evidenza />
        {dati.sessioni && (
          <Riquadro
            titolo="Aperture della pagina"
            valore={numero(sessioniFiltrate.length)}
            nota={`hanno finito in ${numero(finite)} (${percento(finite, sessioniFiltrate.length)})`}
          />
        )}
        {numeriche.map((d) => (
          <Riquadro
            key={d.domanda_id}
            titolo={`Somma: ${d.nome_breve}`}
            valore={numero(d.numero!.somma)}
            nota={`su ${numero(d.numero!.risposte)} risposte · mediana ${numero(d.numero!.mediana)}`}
          />
        ))}
        <Riquadro
          titolo="Consensi agli aggiornamenti"
          valore={numero(confermati)}
          nota={`confermati, su ${numero(statistiche.consenso_richiesto)} richiesti`}
        />
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
        <Paper variant="outlined" sx={{ p: 2, flex: 1, borderColor: tokens.color.line }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Da dove arrivano</Typography>
          {righeProvenienza.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.color.mist }}>Nessun dato nel periodo.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provenienza</TableCell>
                  {dati.sessioni && <TableCell align="right">Aperture</TableCell>}
                  {dati.sessioni && <TableCell align="right">Hanno finito</TableCell>}
                  <TableCell align="right">Iscritti</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {righeProvenienza.map(([p, r]) => (
                  <TableRow key={p}>
                    <TableCell>{p}</TableCell>
                    {dati.sessioni && <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{numero(r.aperture)}</TableCell>}
                    {dati.sessioni && <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{numero(r.finite)} ({percento(r.finite, r.aperture)})</TableCell>}
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{numero(r.iscritti)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, borderColor: tokens.color.line }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Iscritti per giorno</Typography>
          <ColonneGiornaliere
            giorni={statistiche.per_giorno.map((g) => ({
              giorno: g.giorno,
              valore: g.conteggio,
              dettaglio: `${g.giorno.split('-').reverse().join('/')}: ${numero(g.conteggio)} iscritti${dati.sessioni ? `, ${numero(aperturePerGiorno.get(g.giorno) ?? 0)} aperture` : ''}`,
            }))}
          />
        </Paper>
      </Stack>

      {statistiche.domande.map((d, i) => (
        <SchedaDomanda key={d.domanda_id} indice={i} statistiche={d} />
      ))}
    </Stack>
  );
};

const SchedaDomanda: React.FC<{ indice: number; statistiche: StatisticheDomanda }> = ({ indice, statistiche: d }) => {
  const [tuttiITesti, setTuttiITesti] = useState(false);
  const testi = d.testi ?? [];
  const mostrati = tuttiITesti ? testi : testi.slice(0, 8);
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.color.line }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight={700}>{indice + 1}. {d.testo}</Typography>
        <Typography variant="caption" sx={{ color: tokens.color.ash }}>
          {TIPO_DOMANDA_LABELS[d.tipo]} · {numero(d.risposte)} {d.risposte === 1 ? 'risposta' : 'risposte'}
        </Typography>
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        {d.opzioni && (
          <BarreOrizzontali
            righe={d.opzioni.map((o) => ({
              chiave: o.id,
              etichetta: o.etichetta,
              valore: o.conteggio,
              testoValore: `${numero(o.conteggio)} · ${o.percentuale.toLocaleString('it-IT')}%`,
              dettaglio: `${o.etichetta}: ${numero(o.conteggio)} su ${numero(d.risposte)} (${o.percentuale.toLocaleString('it-IT')}%)`,
            }))}
          />
        )}
        {d.tipo === 'scelta_multipla' && d.opzioni && (
          <Typography variant="caption" sx={{ display: 'block', color: tokens.color.ash, mt: 1 }}>
            Percentuali su chi ha risposto: con più scelte la somma supera il 100%.
          </Typography>
        )}
        {d.numero && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {[
                ['Somma', d.numero.somma],
                ['Media', d.numero.media],
                ['Mediana', d.numero.mediana],
                ['Minimo', d.numero.minimo],
                ['Massimo', d.numero.massimo],
              ].map(([nome, valore]) => (
                <Box key={nome as string}>
                  <Typography variant="caption" sx={{ color: tokens.color.ash }}>{nome}</Typography>
                  <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{(valore as number).toLocaleString('it-IT')}</Typography>
                </Box>
              ))}
            </Stack>
            <BarreOrizzontali
              righe={d.numero.fasce.map((f) => ({
                chiave: f.etichetta,
                etichetta: f.etichetta,
                valore: f.conteggio,
                testoValore: numero(f.conteggio),
                dettaglio: `${f.etichetta}: ${numero(f.conteggio)} ${f.conteggio === 1 ? 'risposta' : 'risposte'}`,
              }))}
            />
          </Stack>
        )}
        {testi.length > 0 && (
          <Box sx={{ mt: d.opzioni || d.numero ? 2 : 0 }}>
            {(d.opzioni || d.numero) && (
              <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>Testi scritti</Typography>
            )}
            <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {mostrati.map((t, i) => (
                <Typography component="li" variant="body2" key={`${t.response_id}-${i}`} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {t.testo}
                </Typography>
              ))}
            </Stack>
            {testi.length > mostrati.length && (
              <Button size="small" onClick={() => setTuttiITesti(true)} sx={{ mt: 0.5 }}>Mostra tutti ({numero(testi.length)})</Button>
            )}
          </Box>
        )}
        {!d.opzioni && !d.numero && testi.length === 0 && (
          <Typography variant="body2" sx={{ color: tokens.color.mist }}>Nessuna risposta nel periodo.</Typography>
        )}
      </Box>
    </Paper>
  );
};
